import { useState, useEffect, type ReactNode } from "react";
import Icon from "@/components/ui/Icon";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";
const SOLANA_NETWORK = import.meta.env.VITE_SOLANA_NETWORK || "devnet";
const IS_DEVNET = SOLANA_NETWORK === "devnet";

/* ─── Types ─── */
interface MemberBuyResult {
    member: string;
    managedWallet: string;
    solSpent: number;
    tokensReceived: string;
    txSignature: string;
    txUrl: string | null;
    status: "pending" | "success" | "failed";
    provider: "meteora" | "jupiter";
    errorMessage?: string;
}

interface LaunchData {
    status:
    | "not_started"
    | "pending"
    | "creating"
    | "buying"
    | "locking"
    | "live"
    | "failed";
    tokenMint: string | null;
    tokenName?: string;
    tokenSymbol?: string;
    tokenImageUrl?: string;
    anonTxSignature?: string;
    anonTxUrl?: string;
    totalMembers?: number;
    buyCompletedCount?: number;
    buyFailedCount?: number;
    memberBuyResults?: MemberBuyResult[];
    errorMessage?: string;
}

/* ─── Pipeline Steps ─── */
const PIPELINE_STEPS = [
    { key: "creating", icon: "create-token", label: "Creating\nToken" },
    { key: "buying", icon: "shopping-cart", label: "Buying\nTokens" },
    { key: "locking", icon: "lock-close", label: "Locking\nTokens" },
    { key: "live", icon: "rocket-ship", label: "Codev\nLive" },
] as const;

function getStepIndex(status: string): number {
    const idx = PIPELINE_STEPS.findIndex((s) => s.key === status);
    return idx >= 0 ? idx : -1;
}

/* ═══════════════════════════════════════════════════════════
   LaunchStatusBanner
   ═══════════════════════════════════════════════════════════ */
export default function LaunchStatusBanner({
    cabalSlug,
}: {
    cabalSlug: string;
    tokenMint?: string;
}): ReactNode {
    const [launch, setLaunch] = useState<LaunchData | null>(null);

    useEffect(() => {
        if (!cabalSlug) return;

        let cancelled = false;

        async function poll() {
            try {
                const res = await fetch(
                    `${API_BASE}/api/cabals/${cabalSlug}/launch`
                );
                if (res.ok && !cancelled) {
                    const data = await res.json();
                    setLaunch(data);
                }
            } catch {
                // silently fail
            }
        }

        poll();
        const interval = setInterval(poll, 5000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [cabalSlug]);

    if (!launch || launch.status === "not_started" || launch.status === "live") return null;

    const currentStep = getStepIndex(launch.status);
    const isFailed = launch.status === "failed";

    return (
        <div className="mb-4">
            <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.1)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] overflow-hidden">

                {/* ─── Header ─── */}
                <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-2">
                        <Icon name="rocket-ship" size={16} />
                        <p className={`text-[14px] font-semibold font-[family-name:var(--font-display)] ${isFailed ? "text-red-400" : "text-[#f3ce28]"
                            }`}>
                            {isFailed ? "Launch Failed" : "Launching..."}
                        </p>
                    </div>
                    {launch.tokenMint && (
                        <a
                            href={`https://solscan.io/token/${launch.tokenMint}${IS_DEVNET ? "?cluster=devnet" : ""}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[14px] font-semibold text-white font-[family-name:var(--font-display)] hover:text-white/80 transition-colors"
                        >
                            {launch.tokenMint.slice(0, 3)}...{launch.tokenMint.slice(-3)}
                        </a>
                    )}
                </div>

                {/* ─── Pipeline Content ─── */}
                <div className="bg-[rgba(255,255,255,0.02)] border-t-[0.5px] border-b-[0.5px] border-[rgba(255,255,255,0.2)] p-4">

                    {/* Step indicators */}
                    <div className="flex items-start justify-center gap-4 mb-5">
                        {PIPELINE_STEPS.map((step, i) => {
                            const isComplete = i < currentStep;
                            const isActive = i === currentStep && !isFailed;
                            let iconBg: string;
                            let textColor: string;

                            if (isComplete) {
                                iconBg = "bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.5)] border border-[rgba(255,255,255,0.2)]";
                                textColor = "text-white";
                            } else if (isActive) {
                                iconBg = "bg-gradient-to-b from-[20%] from-[rgba(243,206,40,0)] to-[rgba(243,206,40,0.3)] border border-[rgba(255,255,255,0.2)]";
                                textColor = "text-[#f3ce28]";
                            } else {
                                iconBg = "bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.5)] border border-[rgba(255,255,255,0.2)] opacity-30";
                                textColor = "text-white/50";
                            }

                            return (
                                <div key={step.key} className="flex flex-col items-center gap-2 w-[83px]">
                                    <div className={`rounded-full p-[10px] ${iconBg}`}>
                                        <Icon name={isComplete ? "check-circle" : step.icon} size={24} />
                                    </div>
                                    <p className={`text-[12px] font-semibold text-center leading-normal whitespace-pre-line font-[family-name:var(--font-display)] ${textColor}`}>
                                        {step.label}
                                    </p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Divider */}
                    <div className="h-px w-full bg-white/5 mb-5" />

                    {/* Buy Progress */}
                    {launch.totalMembers && launch.totalMembers > 0 && (
                        <div className="mb-5">
                            <div className="flex justify-between items-center mb-5">
                                <p className="text-[14px] font-semibold text-white font-[family-name:var(--font-display)]">
                                    Member Buys
                                </p>
                                <p className="text-[14px] font-semibold text-white/50 font-[family-name:var(--font-display)]">
                                    {(launch.buyCompletedCount || 0) + (launch.buyFailedCount || 0)}/{launch.totalMembers}
                                </p>
                            </div>
                            <div className="h-2 bg-[rgba(255,255,255,0.08)] rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[#f3ce28] rounded-full transition-all duration-500"
                                    style={{
                                        width: `${(((launch.buyCompletedCount || 0) + (launch.buyFailedCount || 0)) / launch.totalMembers) * 100}%`,
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Error */}
                    {isFailed && launch.errorMessage && (
                        <div className="bg-red-500/10 border-[0.5px] border-red-500/20 rounded-[12px] p-3 mb-5">
                            <p className="text-[10px] text-red-400 font-mono break-all">
                                {launch.errorMessage}
                            </p>
                        </div>
                    )}

                    {/* Divider */}
                    <div className="h-px w-full bg-white/5 mb-5" />

                    {/* Creation tx link */}
                    {launch.anonTxUrl && (
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-[6px]">
                                <Icon name="receipt" size={12} className="opacity-60" />
                                <p className="text-[10px] font-semibold text-white font-[family-name:var(--font-display)]">
                                    Token Creation tx
                                </p>
                            </div>
                            <a
                                href={launch.anonTxUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:opacity-80 transition-opacity"
                            >
                                <Icon name="external-link" size={16} className="opacity-60" />
                            </a>
                        </div>
                    )}
                </div>

                {/* ─── Show Member Purchases ─── */}
                {/* {launch.memberBuyResults && launch.memberBuyResults.length > 0 && (
                    <>
                        <button
                            onClick={() => setExpanded(!expanded)}
                            className="w-full p-4 flex items-center justify-center gap-1 hover:bg-white/[0.02] transition-colors"
                        >
                            <p className="text-[14px] font-semibold text-white font-[family-name:var(--font-display)]">
                                Show Member Purchases
                            </p>
                            <Icon name="chevron-down" size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                        </button>

                        {expanded && (
                            <div className="border-t-[0.5px] border-[rgba(255,255,255,0.1)] px-4 py-3 space-y-2">
                                {launch.memberBuyResults.map((buy, i) => (
                                    <div
                                        key={i}
                                        className={`flex items-center justify-between p-3 rounded-[8px] ${buy.status === "success"
                                                ? "bg-[#f3ce28]/5"
                                                : buy.status === "failed"
                                                    ? "bg-red-500/5"
                                                    : "bg-white/5"
                                            }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Icon
                                                name={buy.status === "success" ? "check-circle" : "receipt"}
                                                size={14}
                                                className={buy.status === "failed" ? "opacity-30" : "opacity-60"}
                                            />
                                            <div>
                                                <p className="text-[10px] font-mono text-white/60">
                                                    {buy.member?.slice(0, 6)}...{buy.member?.slice(-4)}
                                                </p>
                                                {buy.tokensReceived && buy.tokensReceived !== "0" && (
                                                    <p className="text-[9px] text-[#f3ce28]/60 font-[family-name:var(--font-display)]">
                                                        {Number(buy.tokensReceived).toLocaleString()} tokens
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[9px] font-medium text-white/20 font-[family-name:var(--font-display)]">
                                                via {buy.provider}
                                            </span>
                                            {buy.txUrl && (
                                                <a
                                                    href={buy.txUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="hover:opacity-80 transition-opacity"
                                                >
                                                    <Icon name="external-link" size={14} className="opacity-40" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )} */}
            </div>
        </div>
    );
}
